import { LoadingButton } from "@mui/lab";
import Visibility from "@mui/icons-material/Visibility";
import VisibilityOff from "@mui/icons-material/VisibilityOff";
import { Box, FormHelperText, IconButton, InputAdornment } from "@mui/material";
import AuthCard from "components/authentication/AuthCard";
import FlexBox from "components/FlexBox";
import LightTextField from "components/LightTextField";
import { Small } from "components/Typography";
import { useFormik } from "formik";
import useAuth from "hooks/useAuth";
import { FC, useState } from "react";
import toast from "react-hot-toast";
import { Link, useNavigate } from "react-router-dom";
import * as Yup from "yup";

const inputSx = {
  "& .MuiOutlinedInput-root": { borderRadius: 2, height: 56, fontSize: "1rem" },
};

const validationSchema = Yup.object().shape({
  email: Yup.string().email("Deve ser um e-mail válido").max(255),
  password: Yup.string().min(6, "A senha deve ter no mínimo 6 caracteres"),
});

const Login: FC = () => {
  const { login } = useAuth();
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const navigate = useNavigate();

  const { errors, values, touched, handleBlur, handleChange, handleSubmit } = useFormik({
    initialValues: { email: "", password: "", submit: null },
    validationSchema,
    onSubmit: (values: any) => {
      setLoading(true);
      login(values.email, values.password)
        .then(() => {
          setLoading(false);
          toast.success("Login efetuado com sucesso");
          navigate("/dashboard");
        })
        .catch((error) => {
          if (error.errors) setError(error.errors[0]);
          setLoading(false);
        });
    },
  });

  return (
    <AuthCard title="Bem-vindo de volta!">
      <form noValidate onSubmit={handleSubmit}>
        <LightTextField
          fullWidth
          name="email"
          type="email"
          label="E-mail"
          inputMode="email"
          autoComplete="email"
          onBlur={handleBlur}
          onChange={handleChange}
          value={values.email}
          error={Boolean(touched.email && errors.email)}
          helperText={touched.email && errors.email}
          sx={inputSx}
        />

        <Box mt={2.5}>
          <LightTextField
            fullWidth
            name="password"
            type={showPassword ? "text" : "password"}
            label="Senha"
            autoComplete="current-password"
            onBlur={handleBlur}
            onChange={handleChange}
            value={values.password}
            error={Boolean(touched.password && errors.password)}
            helperText={touched.password && errors.password}
            sx={inputSx}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton
                    onClick={() => setShowPassword((v) => !v)}
                    edge="end"
                    aria-label={showPassword ? "Ocultar senha" : "Mostrar senha"}
                  >
                    {showPassword ? <VisibilityOff fontSize="small" /> : <Visibility fontSize="small" />}
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />
        </Box>

        <FlexBox mt={1.5} justifyContent="flex-end">
          <Link to="/forget-password">
            <Small color="primary.main">Esqueci minha senha</Small>
          </Link>
        </FlexBox>

        {error && (
          <FormHelperText
            error
            sx={{ mt: 1, fontSize: 13, fontWeight: 500, textAlign: "center" }}
          >
            {error}
          </FormHelperText>
        )}

        <Box mt={3}>
          <LoadingButton
            loading={loading}
            type="submit"
            fullWidth
            variant="contained"
            sx={{ height: 48, borderRadius: 2, textTransform: "none", fontSize: "1rem" }}
          >
            Entrar
          </LoadingButton>
        </Box>

        <FlexBox mt={2} justifyContent="center">
          <Small color="text.secondary">
            Não tem uma conta?{" "}
            <Link to="/cadastro">
              <Small color="primary.main">Cadastre sua empresa</Small>
            </Link>
          </Small>
        </FlexBox>
      </form>
    </AuthCard>
  );
};

export default Login;
